'use client'

import Image from 'next/image'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import { Autoplay } from 'swiper/modules'
import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { fetchSearchStoreProducts } from '@/utils/loaders'
import { useLanguage } from '@/contexts/LanguageContext'
import { useLanguageRefetch } from '@/hooks/useLanguageRefetch'
import { useTranslations } from 'next-intl'
import defaultImage from '@/assets/icons/NewHomePage/default-event.png'
import Reyal from '@/assets/imgs/store/categories/reyalBlo.svg'
import SearchInput from '@/components/inputs/SearchInput'
import Link from 'next/link'
export default function StoreSliderHero({
  data,
  events: defaultEvents,
  showSearchInput = false,
  clickable,
  position,
  searchId,
}: any) {
  const swiperRef = useRef<any>(null)
  const [showSearchResults, setShowSearchResults] = useState(false)
  const [searchKey, setSearchKey] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [imageErrors, setImageErrors] = useState<{ [key: number]: boolean }>({})
  const t = useTranslations('store')

  const products = (showSearchResults ? searchResults : defaultEvents) || []

  const fetchSearchProducts = async () => {
    if (!showSearchInput) return

    try {
      const res = await fetchSearchStoreProducts(
        `search=${encodeURIComponent(searchKey)}&limit=20&page=1`,
      )
      setSearchResults(res.data?.items || [])
    } catch (error) {
      setSearchResults([])
    }
  }

  useLanguageRefetch({
    refetchFunction: fetchSearchProducts,
    dependencies: [searchKey],
  })

  const searchRef = useRef<HTMLDivElement>(null)
  const router = useRouter()
  const { language } = useLanguage()
  const isRTL = language === 'ar'

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowSearchResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const isVideo = (url: string) => {
    if (!url) return false
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv']
    return videoExtensions.some((ext) => url.toLowerCase().includes(ext))
  }

  useEffect(() => {
    if (!swiperRef.current) return

    const swiper = swiperRef.current
    if (typeof swiper.changeLanguageDirection === 'function') {
      swiper.changeLanguageDirection(isRTL ? 'rtl' : 'ltr')
    }

    swiper.update()
    if (swiper.params?.loop) {
      swiper.slideToLoop(swiper.realIndex, 0, false)
    }
  }, [isRTL, data?.length])

  return (
    <div className='container  px-0 md:px-5 mx-auto py-3 lg:py-6'>
      <div

        className='cursor-pointer'
      >
        <Swiper
          key={`hero-swiper-${language}`}
          modules={[Autoplay]}
          slidesPerView={1}
          spaceBetween={5}
          dir={isRTL ? 'rtl' : 'ltr'}
          loop={data?.length > 1}
          autoplay={{
            delay: data?.[0]?.time * 1000 || 6000,
            disableOnInteraction: false,
          }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper
          }}
        >
          {data?.map((item: any, index: number) => (
            <SwiperSlide key={index}>
              <Link
                href={item?.url || "/store"}
                className={`md:rounded-[30px] w-full ${clickable ? 'cursor-pointer md:h-[161px] lg:h-[350px]' : 'md:h-[384px] lg:h-[440px]'}   overflow-hidden`}>
                {isVideo(item.image_web) ? (
                  <video
                    src={item.image_web}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className='w-full    md:h-[384px] lg:h-[440px] object-cover md:rounded-[30px]'
                  />
                ) : (
                  <Image
                    src={
                      imageErrors[index]
                        ? defaultImage
                        : item.image_web || defaultImage
                    }
                    width={500}
                    height={500}

                    alt='web_image'
                    priority={index === 0}
                    className={`${clickable ? 'cursor-pointer md:h-[161px] lg:h-[350px]' : 'md:h-[384px] lg:h-[380px] '} md:rounded-[30px] w-full  object-fill `}
                    onError={() =>
                      setImageErrors((prev) => ({
                        ...prev,
                        [index]: true,
                      }))
                    }
                  />
                )}
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {showSearchInput && (
        <div className='block lg:hidden w-full mt-4'>
          <div ref={searchRef} className='relative w-full lg:max-w-[400px]'>
            <SearchInput
              isCart={true}
              placeholder={t('searchPlaceholder')}
              className='w-full'
              onFocus={async () => {
                setShowSearchResults(true)
                await fetchSearchProducts()
              }}
              onChange={(e: any) => {
                setTimeout(() => {
                  setSearchKey(e)
                }, 1000)
              }}
            />

            {showSearchResults && (
              <div className='absolute left-0 right-0 top-full z-50 mt-2'>
                <div className='glass-effect rounded-[20px] shadow-lg border overflow-hidden text-start'>
                  <div className='px-6 py-4 border-b'>
                    <h3 className='text-sm font-semibold text-[#D1D3D4]'>
                      {t('viewAll')}
                    </h3>
                  </div>

                  <div className='max-h-[400px] overflow-y-auto'>
                    {products.map((product: any, index: number) => (
                      <div
                        key={index}
                        onClick={() => {
                          router.push(`/store/product-details/${product?.id}`)
                        }}
                        className='block hover:bg-[#111111] transition-colors'
                      >
                        <div className='p-4 border-b'>
                          <div className='flex items-start gap-4'>
                            <img
                              className='w-[100px] h-[100px] rounded-[16px] object-cover'
                              src={product.image || defaultImage.src}
                              onError={(e) => {
                                ; (e.target as HTMLImageElement).src =
                                  defaultImage.src
                              }}
                              alt={product.name || 'product'}
                            />

                            <div className='flex-1'>
                              <div className='flex items-start justify-between gap-2 mb-3'>
                                <h4 className='text-[#D1D3D4] font-medium'>
                                  {product.name}
                                </h4>
                                {product?.label && (
                                  <span className='shrink-0 rounded-[8px] bg-[#d9ac4f]/10 px-2 py-1 text-[10px] font-medium text-[#d9ac4f]'>
                                    {product.label}
                                  </span>
                                )}
                              </div>

                              <div className='flex items-center gap-2 text-sm'>
                                <span className='font-bold text-[#D1D3D4]'>
                                  {product.price}
                                </span>
                                {product?.old_price && (
                                  <span className='font-bold text-[#D1D3D4] line-through'>
                                    {product.old_price}
                                  </span>
                                )}
                                <Image
                                  src={Reyal}
                                  alt='reyal'
                                  width={14}
                                  height={14}
                                  className='h-[14px] w-[14px]'
                                />
                              </div>

                              {typeof product?.stock !== 'undefined' && (
                                <div className='mt-2 text-xs text-[#D1D3D4]'>
                                  {product.stock}
                                </div>
                              )}
                            </div>

                            <div
                              className={`${language === 'ar' ? 'rotate-180' : ''}`}
                            >
                              <svg
                                width='24'
                                height='24'
                                viewBox='0 0 24 24'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                              >
                                <path
                                  d='M9 18L15 12L9 6'
                                  stroke='#d9ac4f'
                                  strokeWidth='2'
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}